import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatStepperModule } from '@angular/material/stepper';
import {
  DtoFilterValues,
  DtoFormData,
  DtoReferenceCategory,
  DtoSupportedLanguage,
  DtoSupportedLanguages,
  IFilterItemDto,
  IKeyDisplayValuePair,
  Language,
  ReferenceCategoryType,
  ReferenceDocumentType,
  ReferenceDocumentTypeDto
} from '@common';
import { forkJoin } from 'rxjs';
import { DtoSelfServiceFormData, FormDataService, I18nService, ReferencesService } from '../../../core';
import { FormDataPipe } from '../../../shared';
import { ContentStepComponent } from './content-step/content-step.component';
import { GenerationStepComponent } from './generation-step/generation-step.component';
import { LanguageStepComponent } from './language-step/language-step.component';
import { PreselectionStepComponent } from './preselection-step/preselection-step.component';

@Component({
  selector: 'app-self-service-form',
  imports: [
    CommonModule,
    ContentStepComponent,
    FormDataPipe,
    FormsModule,
    GenerationStepComponent,
    LanguageStepComponent,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatOptionModule,
    MatSelectModule,
    MatStepperModule,
    ReactiveFormsModule,
    PreselectionStepComponent
  ],
  templateUrl: './self-service-form.component.html',
  styleUrls: ['./self-service-form.component.scss'],
  providers: [
    {
      provide: STEPPER_GLOBAL_OPTIONS,
      useValue: { showError: true }
    }
  ]
})
export class SelfServiceFormComponent implements OnInit {
  //#region @Input ------------------------------------------------------------
  @Input({ required: true }) currentLanguage!: Language;
  @Input({ required: true }) brand!: boolean;
  //#endregion ----------------------------------------------------------------

  //#region private fields ----------------------------------------------------
  private readonly formDataService: FormDataService;
  private readonly referencesService: ReferencesService;
  private readonly i18nService: I18nService;
  //#endregion

  //#region Protected Fields --------------------------------------------------
  protected readonly languageForm: UntypedFormGroup;
  protected readonly languageControl: FormControl<IKeyDisplayValuePair<Language> | null>;
  protected readonly contentForm: UntypedFormGroup;
  protected readonly contentControl: FormControl<IKeyDisplayValuePair<ReferenceDocumentType> | null>;
  protected readonly infoPageControl: FormControl<IKeyDisplayValuePair<ReferenceCategoryType> | null>;
  protected readonly brandControl: FormControl<IKeyDisplayValuePair<string> | null>;
  protected formData!: DtoSelfServiceFormData;
  protected documentTypeSelectItems!: Array<IKeyDisplayValuePair<ReferenceDocumentType>>;
  protected infoPageSelectItems!: Array<IKeyDisplayValuePair<ReferenceCategoryType>>;
  protected languageSelectItems!: Array<IKeyDisplayValuePair<Language>>;
  protected supportedCategories!: Array<DtoReferenceCategory>;
  protected referenceDocumentTypes!: Array<ReferenceDocumentTypeDto>;
  protected filterValues!: DtoFilterValues;
  protected selectedFilters!: DtoFilterValues;
  //#endregion

  //#region Constructor & C° --------------------------------------------------
  public constructor(
    formBuilder: UntypedFormBuilder,
    formDataService: FormDataService,
    i18nService: I18nService,
    referencesService: ReferencesService
  ) {
    // --- Dependency injection ---
    this.formDataService = formDataService;
    this.i18nService = i18nService;
    this.referencesService = referencesService;

    // --- initialize ---
    this.selectedFilters = {
      categories: new Array<IFilterItemDto>(),
      customers: new Array<IFilterItemDto>(),
      roles: new Array<IFilterItemDto>(),
      keywords: new Array<IFilterItemDto>()
    };
    this.filterValues = {
      categories: new Array<IFilterItemDto>(),
      customers: new Array<IFilterItemDto>(),
      roles: new Array<IFilterItemDto>(),
      keywords: new Array<IFilterItemDto>()
    };

    // --- create forms and controls ---
    this.infoPageControl = new FormControl<IKeyDisplayValuePair<ReferenceCategoryType> | null>(null, [
      Validators.required
    ]);
    this.contentControl = new FormControl<IKeyDisplayValuePair<ReferenceDocumentType>>(null, [Validators.required]);
    this.brandControl = new FormControl<IKeyDisplayValuePair<string> | null>(null);
    this.contentForm = formBuilder.group({
      infoPageControl: this.infoPageControl,
      contentControl: this.contentControl,
      brandControl: this.brandControl
    });

    this.languageControl = new FormControl<IKeyDisplayValuePair<Language> | null>(null, Validators.required);
    this.languageControl.valueChanges.subscribe((value: IKeyDisplayValuePair<Language> | null) => {
      if (value != null) {
        this.documentLanguageChanged(value);
      }
    });
    this.languageForm = formBuilder.group({
      languageControl: this.languageControl
    });
  }

  public ngOnInit(): void {
    forkJoin([
      this.referencesService.fetchSupportedCategories(),
      this.referencesService.fetchReferenceDocumentTypes(),
      this.i18nService.getSupportedLanguages()
    ]).subscribe(
      ([categories, documentTypes, supportedLanguages]: [
        Array<DtoReferenceCategory>,
        Array<ReferenceDocumentTypeDto>,
        DtoSupportedLanguages
      ]) => {
        this.supportedCategories = categories;
        this.referenceDocumentTypes = documentTypes;
        this.setDocumentTypeSelectItems(this.currentLanguage);
        this.contentControl.setValue(this.documentTypeSelectItems[0]);
        this.languageSelectItems = supportedLanguages.languages
          .filter((lng: DtoSupportedLanguage) => lng.documentLanguage)
          .map((lng: DtoSupportedLanguage) => {
            return { key: lng.language, displayValue: lng.label[this.currentLanguage] };
          });
        // --- Set document language selector: current language if supported, otherwise english ---
        const current = this.languageSelectItems.find(
          (lng: IKeyDisplayValuePair<Language>) => lng.key == this.currentLanguage
        );
        if (current) {
          this.languageControl.setValue(current);
        } else {
          const english = this.languageSelectItems.find(
            (lng: IKeyDisplayValuePair<Language>) => lng.key == Language.en
          );
          if (english) {
            this.languageControl.setValue(english);
          }
        }
      }
    );

    this.formDataService.getSelfServiceFormData(this.currentLanguage).subscribe((data: DtoFormData) => {
      this.formData = data.data;
    });
  }
  //#endregion

  //#region UI triggered methods ----------------------------------------------
  protected preselectionChanged(newSelection: DtoFilterValues): void {
    this.selectedFilters = { ...newSelection };
  }

  private documentLanguageChanged(value: IKeyDisplayValuePair<Language>): void {
    this.setInfoPageSelectItems(value.key);
    this.referencesService.fetchFilterValues(value.key).subscribe((data: DtoFilterValues) => {
      this.filterValues = data;
      this.selectedFilters.categories = this.setSelectionInNewLanguage(
        this.selectedFilters.categories,
        this.filterValues.categories
      );
      this.selectedFilters.roles = this.setSelectionInNewLanguage(this.selectedFilters.roles, this.filterValues.roles);
      this.selectedFilters.keywords = this.setSelectionInNewLanguage(
        this.selectedFilters.keywords,
        this.filterValues.keywords
      );
      this.infoPageControl.setValue(
        this.infoPageSelectItems.find(
          (category: IKeyDisplayValuePair<ReferenceCategoryType>) => category.key == this.infoPageControl.value?.key
        ) || null
      );
    });
  }
  //#endregion

  //#region Auxiliary methods -------------------------------------------------
  private setInfoPageSelectItems(language: Language): void {
    this.infoPageSelectItems = this.supportedCategories.map((category: DtoReferenceCategory) => {
      return { key: category.category, displayValue: category.label[language] };
    });
  }

  private setDocumentTypeSelectItems(language: Language): void {
    this.documentTypeSelectItems = this.referenceDocumentTypes.map((type: ReferenceDocumentTypeDto) => {
      return { key: type.type, displayValue: type.label[language] };
    });
  }

  private setSelectionInNewLanguage(
    previousSelection: Array<IFilterItemDto>,
    newItems: Array<IFilterItemDto>
  ): Array<IFilterItemDto> {
    const result = new Array<IFilterItemDto>(
      ...previousSelection
        .filter(
          (item: IFilterItemDto) => newItems.find((newItem: IFilterItemDto) => newItem.key == item.key) != undefined
        )
        .map((item: IFilterItemDto) => newItems.find((newItem: IFilterItemDto) => newItem.key == item.key)!)
    );
    return result;
  }
  //#endregion
}
